// src/components/PullToRefresh/PullToRefreshIndicator.tsx
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { useState, useEffect } from 'react';

// Elastic bounce animation
// const elasticBounce = keyframes`
//   0% { transform: scale(1); }
//   30% { transform: scale(1.15); }
//   50% { transform: scale(0.95); }
//   70% { transform: scale(1.05); }
//   100% { transform: scale(1); }
// `;

// Spinner rotation
const spinAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

interface Props {
  isRefreshing: boolean;
  pullDistance: number;
  threshold: number;
}

export const PullToRefreshIndicator = ({ isRefreshing, pullDistance, threshold }: Props) => {
  const progress = Math.min((pullDistance / threshold) * 100, 100);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    if (isRefreshing) {
      // Update timestamp when refresh starts
      setLastUpdate(new Date());
    }
  }, [isRefreshing]);

  // Format relative time (Instagram style)
  const getRelativeTime = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Circle progress calculations
  const circleRadius = 18;
  const circleCircumference = 2 * Math.PI * circleRadius;
  const circleOffset = circleCircumference - (progress / 100) * circleCircumference;

  return (
    <Box
      sx={{
        height: isRefreshing ? 'auto' : `${Math.min(pullDistance, 80)}px`,
        overflow: 'hidden',
        transition: isRefreshing ? 'height 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        bgcolor: 'background.paper',
        minHeight: isRefreshing ? '80px' : 0,
        position: 'relative',
      }}
    >
      {/* Linear Progress Bar - Top */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          bgcolor: 'rgba(60, 111, 125, 0.1)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: isRefreshing ? '100%' : `${progress}%`,
            bgcolor: '#3C6F7D',
            transition: isRefreshing ? 'none' : 'width 0.1s ease-out',
            animation: isRefreshing ? `${spinAnimation} 1.5s linear infinite` : 'none',
            transformOrigin: 'left',
          }}
        />
      </Box>

      {(isRefreshing || pullDistance > 0) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            py: 2,
          }}
        >
          {isRefreshing ? (
            <>
              {/* Loading Spinner + Progress Ring */}
              <Box
                sx={{
                  position: 'relative',
                  width: 44,
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Outer spinning ring */}
                <svg
                  width="44"
                  height="44"
                  style={{
                    position: 'absolute',
                    animation: `${spinAnimation} 1s linear infinite`,
                  }}
                >
                  <circle
                    cx="22"
                    cy="22"
                    r="18"
                    fill="none"
                    stroke="#3C6F7D"
                    strokeWidth="3"
                    strokeDasharray={`${circleCircumference * 0.25} ${circleCircumference}`}
                    strokeLinecap="round"
                  />
                </svg>

                {/* Inner static circle - NO ANIMATION */}
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    border: '2px solid rgba(60, 111, 125, 0.2)',
                  }}
                />
              </Box>

              {/* Last update time */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: '11px',
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                Updated {getRelativeTime(lastUpdate)}
              </Typography>
            </>
          ) : (
            <>
              {/* Pull Circle Progress */}
              <Box
                sx={{
                  position: 'relative',
                  width: 44,
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Background circle */}
                <svg width="44" height="44" style={{ position: 'absolute' }}>
                  <circle
                    cx="22"
                    cy="22"
                    r={circleRadius}
                    fill="none"
                    stroke="rgba(60, 111, 125, 0.1)"
                    strokeWidth="3"
                  />
                </svg>

                {/* Progress circle */}
                <svg
                  width="44"
                  height="44"
                  style={{
                    position: 'absolute',
                    transform: 'rotate(-90deg)',
                  }}
                >
                  <circle
                    cx="22"
                    cy="22"
                    r={circleRadius}
                    fill="none"
                    stroke="#3C6F7D"
                    strokeWidth="3"
                    strokeDasharray={circleCircumference}
                    strokeDashoffset={circleOffset}
                    strokeLinecap="round"
                    style={{
                      transition: 'stroke-dashoffset 0.1s ease-out',
                    }}
                  />
                </svg>

                {/* Percentage text */}
                {/* <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#3C6F7D',
                    opacity: progress / 100,
                  }}
                >
                  {Math.floor(progress)}
                </Typography> */}
              </Box>

              {/* Optional: Subtle hint text - hanya muncul di awal */}
              {/* {pullDistance < 30 && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '10px',
                    color: 'text.disabled',
                    opacity: 0.5,
                  }}
                >
                  Pull to refresh
                </Typography>
              )} */}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};